import { useCallback, useMemo, useState } from 'react';
import { StanButton, StanFilters, StanTable } from '@/components';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useEmailRecipients } from '../queries';
import { getEmailRecipientColumns, RECIPIENTS_EXPORT_PAGE_SIZE, RECIPIENTS_PAGE_SIZE } from '../constants';
import {
    buildRecipientsApiParams,
    getEmptyStateMessage,
    getPaginationLabel,
    transformFiltersToApiFormat,
} from '../logic';
import { EMAIL_RECIPIENT_FILTER_CONFIGS } from '../constants/emailRecipientFilterConfigs';
import { ExportCsvModal } from './ExportCsvModal';
import { useModal } from '@/hooks/useModal';
import { fetchEmailRecipientsList } from '@/lib/api/email-flows';
import { exportEmailRecipientsToCSV } from '../utils';
import type { EmailRecipient } from '../types';
import type { StanFilterValue } from '@/components/core-components/StanFilters/types';

interface EmailRecipientsTableProps {
    emailId: number;
    initialPage?: number;
}

export const EmailRecipientsTable = ({ emailId, initialPage = 1 }: EmailRecipientsTableProps) => {
    const { push } = useStanRouter();
    const exportModal = useModal();

    const [currentPage, setCurrentPage] = useState(initialPage);
    const [filterValues, setFilterValues] = useState<StanFilterValue[]>([]);

    // Transform filter values to API format
    const transformedFilters = useMemo(() => transformFiltersToApiFormat(filterValues), [filterValues]);

    // Build API params
    const apiParams = useMemo(
        () => buildRecipientsApiParams(currentPage, RECIPIENTS_PAGE_SIZE, transformedFilters),
        [currentPage, transformedFilters]
    );

    const { data, isLoading, isFetching } = useEmailRecipients(emailId, apiParams);

    const recipients = data?.data || [];
    const totalPages = data?.metadata?.total_pages || 0;

    const columns = useMemo(() => getEmailRecipientColumns(), []);

    const totalRowsLabel = getPaginationLabel(recipients.length, totalPages, RECIPIENTS_PAGE_SIZE, currentPage);

    const { header: emptyHeader, subheader: emptySubheader } = getEmptyStateMessage(filterValues.length > 0);

    const handleRowClick = (recipient: EmailRecipient) => {
        push(`/audience/details/${recipient.user_fan_id}`);
    };

    const handleFilterChange = (newFilterValues: StanFilterValue[]) => {
        setFilterValues(newFilterValues);
        setCurrentPage(1); // Reset to first page when filters change
        push(`/flows/recipients/${emailId}/1`);
    };

    const handleResetFilters = () => {
        setFilterValues([]);
        setCurrentPage(1);
        push(`/flows/recipients/${emailId}/1`);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        push(`/flows/recipients/${emailId}/${page}`);
    };

    const totalExportPages = Math.ceil((totalPages * RECIPIENTS_PAGE_SIZE) / RECIPIENTS_EXPORT_PAGE_SIZE);

    const fetchPageData = useCallback(
        async (page: number): Promise<EmailRecipient[]> => {
            const response = await fetchEmailRecipientsList(emailId, {
                page,
                pageSize: RECIPIENTS_EXPORT_PAGE_SIZE,
                ...(transformedFilters && { filter_array: transformedFilters }),
            });
            return response.data;
        },
        [emailId, transformedFilters]
    );

    const handleCSVDataFetched = useCallback(
        (data: EmailRecipient[]) => {
            const filename = `email-recipients-${emailId}-${new Date().toISOString().split('T')[0]}`;
            exportEmailRecipientsToCSV(data, filename);
        },
        [emailId]
    );

    return (
        <>
            <div className='mb-6 flex w-full items-start justify-between gap-4'>
                <div className='mb-4'>
                    <p className='text-secondary text-label-sm-bold mb-3'>Filter Recipients</p>
                    <StanFilters
                        filters={EMAIL_RECIPIENT_FILTER_CONFIGS}
                        filterValues={filterValues}
                        onFilterChanged={handleFilterChange}
                        onResetFilters={handleResetFilters}
                        placement='bottom-start'
                    />
                </div>

                <StanButton name='email-recipients-page-button' variant='outline' size='xs' onClick={exportModal.open}>
                    Download CSV
                </StanButton>
            </div>

            <StanTable
                columns={columns}
                data={recipients}
                isLoading={isLoading || isFetching}
                page={currentPage}
                totalPages={totalPages}
                totalRowsLabel={totalRowsLabel}
                onPageChange={handlePageChange}
                onRowClick={handleRowClick}
                emptyHeader={emptyHeader}
                emptySubheader={emptySubheader}
                hideHeaderWhenEmpty
            />

            <ExportCsvModal
                isOpen={exportModal.isOpen}
                totalPages={totalExportPages}
                filename={`email-recipients-${emailId}-${new Date().toISOString().split('T')[0]}.csv`}
                onClose={exportModal.close}
                onDataFetched={handleCSVDataFetched}
                fetchPageData={fetchPageData}
            />
        </>
    );
};
